import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Close as CloseIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GppGood as SignIcon,
  MoreVert as MoreIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
  AutorenewRounded as RenewIcon,
  CalendarMonth as MultiMonthIcon,
  Description as PrescriptionIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import SendDocumentDialog from '../SendDocumentDialog';
import SignPrescriptionDialog from './SignPrescriptionDialog';
import ClinicalHtmlView from './ClinicalHtmlView';
import { MemedDocumentActions, MemedOriginChip, isMemedPrescription } from './MemedPrescriptionBadge';
import { useMemed } from '../../hooks/useMemed';
import { useAuth } from '../../contexts/AuthContext';
import {
  deleteMemedPrescription,
  hasStoredMemedPdf,
  openMemedPrescriptionDocument,
} from '../../services/memedService';
import {
  copyTextToClipboard,
  getShareableSignedPdfLink,
  openSignedPrescriptionPdf,
} from '../../services/documentSignatureService';
import {
  hasShareablePrescriptionPdf,
  isSignedPrescription,
  signatureProviderLabel,
} from '../../utils/prescriptionSignature';
import { composePosologyLine } from '../../utils/prescriptionPosologyLine';
import { buildPrescriptionPrintHtml, prescriptionHasMultipleVias } from '../../utils/prescriptionPrintDocument';
import { formatPrescriptionItemQuantity } from '../../utils/prescriptionItemQuantity';
import { printClinicHtml } from '../../utils/clinicPrintHtml';
import {
  buildMultiMonthPrintSettings,
  getMultiMonthCount,
} from '../../utils/prescriptionMultiMonth';
import {
  prescriptionTypeLabel,
  prescriptionTypeColors,
} from '../../utils/prescriptionTypes';
import {
  CLOUD_API_THEME,
  cloudApiCampaignCardSx,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiInputFieldSx,
  cloudApiMenuPaperSx,
  cloudApiOutlinedButtonSx,
  cloudApiPrimaryButtonSx,
  cloudApiSectionTitleSx,
  cloudApiTealIconButtonSx,
} from '../../pages/whatsappCloudApiTheme';

const statusLabels: Record<string, string> = { active: 'Ativa', cancelled: 'Cancelada', expired: 'Expirada' };
const statusColors: Record<string, string> = { active: '#10b981', cancelled: '#ef4444', expired: '#f59e0b' };

export interface PrescriptionActionsBarProps {
  prescription: any;
  patientFallback?: {
    id?: string;
    email?: string;
    phone?: string;
    name?: string;
  };
  onUpdated?: () => void | Promise<void>;
  onNotify?: (message: string) => void;
  /** Quando informado, abre edição in-place (ex.: painel do paciente) em vez de ir à página Receituários. */
  onEdit?: (prescription: any) => void;
  /** Cor dos ícones no painel compacto (opcional). */
  iconSx?: Record<string, unknown>;
}

const PrescriptionActionsBar: React.FC<PrescriptionActionsBarProps> = ({
  prescription: prescriptionProp,
  patientFallback,
  onUpdated,
  onNotify,
  onEdit,
  iconSx,
}) => {
  const navigate = useNavigate();
  const memed = useMemed();
  const { user } = useAuth();

  const [prescription, setPrescription] = useState<any>(prescriptionProp);
  const [loading, setLoading] = useState(false);
  const [renewingId, setRenewingId] = useState<string | null>(null);

  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState<any>(null);
  const [sendOpen, setSendOpen] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewPrescription, setViewPrescription] = useState<any>(null);

  const [printOpen, setPrintOpen] = useState(false);
  const [printData, setPrintData] = useState<any>(null);
  const [printFirstViaOnly, setPrintFirstViaOnly] = useState(false);
  useEffect(() => { setPrintFirstViaOnly(false); }, [printData]);
  const printHasMultipleVias = prescriptionHasMultipleVias(printData?.prescription);

  const [multiMonthOpen, setMultiMonthOpen] = useState(false);
  const [multiMonthTarget, setMultiMonthTarget] = useState<any>(null);
  const [multiMonthValue, setMultiMonthValue] = useState(2);
  const [multiMonthSaving, setMultiMonthSaving] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuPrescription, setMenuPrescription] = useState<any>(null);

  const notify = useCallback((message: string) => {
    onNotify?.(message);
  }, [onNotify]);

  const refreshParent = useCallback(async () => {
    await onUpdated?.();
  }, [onUpdated]);

  const reloadPrescription = useCallback(async (id?: string) => {
    const targetId = id || prescriptionProp?.id;
    if (!targetId) return null;
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/clinic/prescriptions/${targetId}`));
      const data = res.data?.data;
      if (data) {
        setPrescription(data);
        return data;
      }
    } catch {
      notify('Não foi possível carregar o receituário.');
    } finally {
      setLoading(false);
    }
    return null;
  }, [prescriptionProp?.id, notify]);

  useEffect(() => {
    setPrescription(prescriptionProp);
  }, [prescriptionProp]);

  useEffect(() => {
    if (!prescriptionProp?.id) return;
    void reloadPrescription(prescriptionProp.id);
  }, [prescriptionProp?.id, reloadPrescription]);

  const applyMemedDocuments = (prescriptionId: string, changes: Record<string, any>) => {
    setPrescription((prev: any) => (prev?.id === prescriptionId ? { ...prev, ...changes } : prev));
    setViewPrescription((prev: any) => (prev?.id === prescriptionId ? { ...prev, ...changes } : prev));
    if (Array.isArray(changes?.items)) void refreshParent();
  };

  const openSignDialog = (target: any) => {
    if (!target?.professional_id) {
      notify('Defina o profissional prescritor antes de assinar o receituário.');
      return;
    }
    setSignTarget(target);
    setSignOpen(true);
  };

  const handleSigned = async (info: any) => {
    setSignOpen(false);
    setSignTarget(null);
    notify(`Receituário assinado digitalmente. Código de conferência: ${info?.signature_verification_code || '—'}`);
    await reloadPrescription();
    await refreshParent();
    if (info?.id) {
      await openSignedPrescriptionPdf(info.id, info.signed_pdf_url).catch(() => undefined);
    }
  };

  const handleCopySignedPdfLink = async (target: any) => {
    if (!target?.id || !hasShareablePrescriptionPdf(target)) return;
    const url = getShareableSignedPdfLink({
      kind: 'prescription',
      documentId: target.id,
      signedPdfUrl: target.signed_pdf_url,
      signatureVerificationCode: target.signature_verification_code,
    });
    if (!url) {
      notify('Não foi possível gerar o link do PDF assinado. Recarregue o receituário e tente de novo.');
      return;
    }
    const copied = await copyTextToClipboard(url);
    notify(copied ? 'Link do PDF assinado copiado.' : url);
  };

  const handlePrint = async (target: any) => {
    if (!target?.id) return;

    if (isMemedPrescription(target)) {
      const result = await openMemedPrescriptionDocument(target);
      if (result.documents) applyMemedDocuments(target.id, result.documents);
      if (result.opened) return;
      notify(result.message || 'Não foi possível abrir o documento oficial da Memed.');
      return;
    }

    if (isSignedPrescription(target)) {
      try {
        await openSignedPrescriptionPdf(target.id, target.signed_pdf_url);
      } catch (err: any) {
        notify(err?.message || 'Não foi possível abrir o PDF assinado.');
      }
      return;
    }

    try {
      const res = await axios.get(getApiUrl(`/clinic/prescriptions/${target.id}/print`));
      setPrintData(res.data?.data);
      setPrintOpen(true);
    } catch {
      notify('Erro ao gerar impressão');
    }
  };

  const handleView = async (target: any) => {
    if (!target?.id) return;
    try {
      const data = await reloadPrescription(target.id);
      setViewPrescription(data || target);
      setViewOpen(true);
    } catch {
      notify('Erro ao visualizar receituário');
    }
  };

  const executePrint = () => {
    if (!printData) return;
    printClinicHtml(buildPrescriptionPrintHtml(printData, { firstViaOnly: printFirstViaOnly }));
  };

  const openMultiMonthDialog = (target: any) => {
    const current = getMultiMonthCount(target?.print_settings);
    setMultiMonthTarget(target);
    setMultiMonthValue(current > 1 ? current : 2);
    setMultiMonthOpen(true);
  };

  const handleConfirmMultiMonth = async () => {
    if (!multiMonthTarget?.id) return;
    const months = Math.min(12, Math.max(2, Math.floor(Number(multiMonthValue) || 2)));

    setMultiMonthSaving(true);
    try {
      const prescriptionId = multiMonthTarget.id;
      const nextPrintSettings = buildMultiMonthPrintSettings(multiMonthTarget.print_settings, months);
      await axios.put(getApiUrl(`/clinic/prescriptions/${prescriptionId}`), {
        print_settings: nextPrintSettings,
      });
      notify(`Receituário configurado para ${months} meses. Cada mês será impresso em uma página separada.`);
      setMultiMonthOpen(false);
      setMultiMonthTarget(null);
      await reloadPrescription(prescriptionId);
      await refreshParent();
      const printRes = await axios.get(getApiUrl(`/clinic/prescriptions/${prescriptionId}/print`));
      setPrintData(printRes.data?.data);
      setPrintOpen(true);
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Erro ao configurar emissão multi-mês');
    } finally {
      setMultiMonthSaving(false);
    }
  };

  const handleRenew = async (target: any, options: { asLocal?: boolean } = {}) => {
    if (!target?.id) return;

    if (isMemedPrescription(target) && !options.asLocal) {
      const patientId = target.patient_id || patientFallback?.id;
      if (!patientId) {
        notify('Paciente não identificado para renovar na Memed.');
        return;
      }
      setRenewingId(target.id);
      try {
        const full = Array.isArray(target.items) && target.items.length > 0
          ? target
          : (await reloadPrescription(target.id)) || target;
        const ok = await memed.openPrescription({
          patientId,
          professionalId: user?.professional_id || target.professional_id || null,
          appointmentId: target.appointment_id || null,
          medicalRecordId: target.medical_record_id || null,
          items: Array.isArray(full.items) ? full.items : [],
        });
        if (!ok) {
          notify(memed.error || 'Não foi possível abrir a Memed para renovar a receita.');
        }
      } finally {
        setRenewingId(null);
      }
      return;
    }

    const question = isMemedPrescription(target)
      ? 'Criar um receituário próprio do Klinvia com os mesmos medicamentos e a data de hoje? Ele poderá ser impresso ou assinado com certificado ICP-Brasil.'
      : 'Renovar este receituário com a data de hoje? Será criada uma nova receita idêntica para impressão.';
    if (!window.confirm(question)) {
      return;
    }

    setRenewingId(target.id);
    try {
      const res = await axios.post(getApiUrl(`/clinic/prescriptions/${target.id}/renew`));
      const renewed = res.data?.data;
      notify('Receituário renovado com a data de hoje!');
      await refreshParent();
      if (renewed?.id) {
        const printRes = await axios.get(getApiUrl(`/clinic/prescriptions/${renewed.id}/print`));
        setPrintData(printRes.data?.data);
        setPrintOpen(true);
      }
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Erro ao renovar receituário');
    } finally {
      setRenewingId(null);
    }
  };

  const handleDuplicate = async (target: any) => {
    try {
      await axios.post(getApiUrl(`/clinic/prescriptions/${target.id}/duplicate`));
      notify(
        isMemedPrescription(target)
          ? 'Receita Memed duplicada como receituário próprio (editável, pode ser assinado com ICP-Brasil).'
          : 'Receituário duplicado com sucesso!',
      );
      await refreshParent();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Erro ao duplicar');
    }
  };

  const handleCancel = async (id: string) => {
    const reason = window.prompt('Motivo do cancelamento:');
    if (reason === null) return;
    try {
      await axios.post(getApiUrl(`/clinic/prescriptions/${id}/cancel`), { reason });
      notify('Receituário cancelado');
      await reloadPrescription(id);
      await refreshParent();
    } catch {
      notify('Erro ao cancelar');
    }
  };

  const handleDelete = async (target: any) => {
    const isMemed = isMemedPrescription(target);
    const question = isMemed
      ? 'Excluir esta receita também na Memed? A receita digital do paciente deixará de funcionar.'
      : 'Tem certeza que deseja excluir este receituário?';
    if (!window.confirm(question)) return;
    try {
      if (isMemed) {
        await deleteMemedPrescription(target.id);
      } else {
        await axios.delete(getApiUrl(`/clinic/prescriptions/${target.id}`));
      }
      notify('Receituário excluído');
      await refreshParent();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Erro ao excluir');
    }
  };

  const openSendDialog = () => {
    setSendOpen(true);
  };

  const handleEdit = (target: any) => {
    if (!target?.id) return;
    if (onEdit) {
      onEdit(target);
      return;
    }
    navigate(`/clinic/prescriptions?edit=${target.id}`);
  };

  const p = prescription || prescriptionProp;
  if (!p?.id) return null;

  const canEditInPlace =
    Boolean(onEdit)
    && p.origin !== 'memed'
    && !isMemedPrescription(p)
    && !isSignedPrescription(p);

  const buttonSx = iconSx || cloudApiTealIconButtonSx;
  const patientId = p.patient_id || patientFallback?.id;
  const patientEmail = p.patient_email || patientFallback?.email;
  const patientPhone = p.patient_phone || patientFallback?.phone;
  const patientName = p.patient_name || patientFallback?.name;

  return (
    <>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
        {loading ? <CircularProgress size={16} sx={{ mx: 0.5 }} /> : null}

        <MemedDocumentActions
          prescription={p}
          onUpdated={(docs) => applyMemedDocuments(p.id, docs)}
          onError={notify}
          onSuccess={notify}
        />

        <Tooltip
          title={
            isMemedPrescription(p)
              ? 'Abrir documento oficial Memed'
              : isSignedPrescription(p)
                ? 'Abrir PDF assinado'
                : 'Imprimir'
          }
        >
          <IconButton size="small" onClick={() => void handlePrint(p)} sx={buttonSx}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {p.status === 'active' && !isMemedPrescription(p) && !isSignedPrescription(p) && (
          <Tooltip title="Assinar com certificado digital ICP-Brasil (A1 ou A3)">
            <IconButton size="small" onClick={() => openSignDialog(p)} sx={buttonSx}>
              <SignIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {p.status === 'active' && !isMemedPrescription(p) && !isSignedPrescription(p) && (
          <Tooltip title="Emitir para vários meses (uma página por mês na impressão)">
            <IconButton size="small" onClick={() => openMultiMonthDialog(p)} sx={buttonSx}>
              <MultiMonthIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {p.status === 'active' && (
          <Tooltip title={isMemedPrescription(p) ? 'Renovar na Memed (nova receita digital)' : 'Renovar com data de hoje'}>
            <span>
              <IconButton
                size="small"
                onClick={() => void handleRenew(p)}
                disabled={renewingId === p.id || memed.opening}
                sx={buttonSx}
              >
                {renewingId === p.id ? (
                  <CircularProgress size={16} sx={{ color: CLOUD_API_THEME.teal }} />
                ) : (
                  <RenewIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {canEditInPlace && (
          <Tooltip title="Editar receituário (editor completo)">
            <IconButton size="small" onClick={() => handleEdit(p)} sx={buttonSx}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Visualizar">
          <IconButton size="small" onClick={() => void handleView(p)} sx={buttonSx}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Enviar">
          <IconButton size="small" onClick={openSendDialog} sx={buttonSx}>
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Mais opções">
          <IconButton
            size="small"
            onClick={(e) => {
              setMenuAnchor(e.currentTarget);
              setMenuPrescription(p);
            }}
            sx={buttonSx}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: cloudApiMenuPaperSx }}
      >
        {menuPrescription?.origin !== 'memed' && !isSignedPrescription(menuPrescription) && (
          <MenuItem onClick={() => { if (menuPrescription) handleEdit(menuPrescription); setMenuAnchor(null); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
        )}
        {menuPrescription?.status === 'active'
          && !isMemedPrescription(menuPrescription)
          && !isSignedPrescription(menuPrescription) && (
          <MenuItem onClick={() => { if (menuPrescription) openSignDialog(menuPrescription); setMenuAnchor(null); }}>
            <ListItemIcon><SignIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Assinar com certificado digital</ListItemText>
          </MenuItem>
        )}
        {menuPrescription?.status === 'active' && (
          <MenuItem onClick={() => { if (menuPrescription) void handleRenew(menuPrescription); setMenuAnchor(null); }}>
            <ListItemIcon><RenewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>
              {menuPrescription && isMemedPrescription(menuPrescription)
                ? 'Renovar na Memed (mesmos medicamentos)'
                : 'Renovar com data de hoje'}
            </ListItemText>
          </MenuItem>
        )}
        {menuPrescription && isMemedPrescription(menuPrescription) && (
          <MenuItem onClick={() => { void handleRenew(menuPrescription, { asLocal: true }); setMenuAnchor(null); }}>
            <ListItemIcon><RenewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Renovar como receituário próprio</ListItemText>
          </MenuItem>
        )}
        {menuPrescription?.status === 'active'
          && !isMemedPrescription(menuPrescription)
          && !isSignedPrescription(menuPrescription) && (
          <MenuItem onClick={() => { if (menuPrescription) openMultiMonthDialog(menuPrescription); setMenuAnchor(null); }}>
            <ListItemIcon><MultiMonthIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Emitir para vários meses</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { if (menuPrescription) void handleDuplicate(menuPrescription); setMenuAnchor(null); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (menuPrescription) void handlePrint(menuPrescription); setMenuAnchor(null); }}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>
            {menuPrescription && isMemedPrescription(menuPrescription)
              ? 'Abrir PDF da receita Memed'
              : 'Imprimir'}
          </ListItemText>
        </MenuItem>
        {menuPrescription && hasShareablePrescriptionPdf(menuPrescription) && (
          <MenuItem
            onClick={() => {
              void handleCopySignedPdfLink(menuPrescription);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copiar link do PDF assinado</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { openSendDialog(); setMenuAnchor(null); }}>
          <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Enviar para paciente</ListItemText>
        </MenuItem>
        {menuPrescription?.status === 'active' && (
          <MenuItem onClick={() => { if (menuPrescription) void handleCancel(menuPrescription.id); setMenuAnchor(null); }}>
            <ListItemIcon><CancelIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
            <ListItemText>Cancelar</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => { if (menuPrescription) void handleDelete(menuPrescription); setMenuAnchor(null); }}
          sx={{ color: '#ef4444' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      <SignPrescriptionDialog
        open={signOpen}
        prescription={signTarget}
        onClose={() => { setSignOpen(false); setSignTarget(null); }}
        onSigned={(info) => void handleSigned(info)}
      />

      <SendDocumentDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        documentType="prescription"
        documentId={p.id}
        patientId={patientId}
        patientEmail={patientEmail}
        patientPhone={patientPhone}
        patientWhatsapp={p.patient_whatsapp}
        patientName={patientName}
        documentLabel={
          isSignedPrescription(p)
            ? 'Receituário assinado'
            : hasStoredMemedPdf(p)
              ? 'Receituário Memed (PDF)'
              : 'Receituário'
        }
      />

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
        <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <PrescriptionIcon sx={cloudApiDialogHeaderIconSx} />
            Receituário {viewPrescription?.prescription_number}
            {viewPrescription && (
              <>
                <Chip
                  label={prescriptionTypeLabel(viewPrescription.prescription_type)}
                  size="small"
                  sx={{
                    bgcolor: `${prescriptionTypeColors[viewPrescription.prescription_type] || prescriptionTypeColors.special_control}20`,
                    color: prescriptionTypeColors[viewPrescription.prescription_type] || prescriptionTypeColors.special_control,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={statusLabels[viewPrescription.status]}
                  size="small"
                  sx={{ bgcolor: `${statusColors[viewPrescription.status]}20`, color: statusColors[viewPrescription.status], fontWeight: 600 }}
                />
                {getMultiMonthCount(viewPrescription.print_settings) > 1 && (
                  <Chip label={`${getMultiMonthCount(viewPrescription.print_settings)} meses`} size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600 }} />
                )}
                <MemedOriginChip prescription={viewPrescription} />
                {isSignedPrescription(viewPrescription) && (
                  <Chip
                    icon={<SignIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label="Assinada digitalmente"
                    size="small"
                    sx={{ bgcolor: CLOUD_API_THEME.tealSoft, color: CLOUD_API_THEME.tealDark, fontWeight: 600 }}
                  />
                )}
              </>
            )}
          </Box>
          <IconButton onClick={() => setViewOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...cloudApiDialogContentSx, p: 3 }}>
          {viewPrescription && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ ...cloudApiSectionTitleSx, mb: 1 }}>Paciente</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewPrescription.patient_name}</Typography>
                  {viewPrescription.patient_cpf && <Typography variant="body2"><strong>CPF:</strong> {viewPrescription.patient_cpf}</Typography>}
                  {viewPrescription.patient_phone && <Typography variant="body2"><strong>Telefone:</strong> {viewPrescription.patient_phone}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ ...cloudApiSectionTitleSx, mb: 1 }}>Profissional</Typography>
                  <Typography variant="body2"><strong>Nome:</strong> {viewPrescription.professional_name || '-'}</Typography>
                  {viewPrescription.professional_registration && (
                    <Typography variant="body2"><strong>Registro:</strong> {viewPrescription.professional_registration}</Typography>
                  )}
                </Grid>
              </Grid>

              {isSignedPrescription(viewPrescription) && (
                <Alert
                  severity="success"
                  icon={<SignIcon />}
                  sx={{ mb: 2, borderRadius: 2 }}
                  action={(
                    <Button
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={() => void handlePrint(viewPrescription)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Abrir PDF
                    </Button>
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Assinada por {viewPrescription.signer_name || viewPrescription.professional_name} em{' '}
                    {viewPrescription.signed_at
                      ? new Date(viewPrescription.signed_at).toLocaleString('pt-BR')
                      : '—'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                    {signatureProviderLabel(viewPrescription.signature_provider)}{' '}
                    ICP-Brasil · código de conferência{' '}
                    <strong>{viewPrescription.signature_verification_code || '—'}</strong>
                  </Typography>
                </Alert>
              )}

              {isMemedPrescription(viewPrescription) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Esta receita foi emitida na Memed{viewPrescription.memed_signed ? ' e assinada digitalmente' : ''}.
                  {' '}
                  {hasStoredMemedPdf(viewPrescription)
                    ? 'O PDF está guardado no Klinvia e é o documento enviado ao paciente.'
                    : 'O PDF ainda não foi guardado no Klinvia — use o botão de sincronizar.'}
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MemedDocumentActions
                      prescription={viewPrescription}
                      onUpdated={(docs) => applyMemedDocuments(viewPrescription.id, docs)}
                      onError={notify}
                      onSuccess={notify}
                    />
                  </Box>
                </Alert>
              )}

              <Typography variant="subtitle2" sx={{ ...cloudApiSectionTitleSx, mb: 1 }}>
                {viewPrescription.format === 'free_text' ? 'Conteúdo' : 'Medicamentos Prescritos'}
              </Typography>
              {viewPrescription.format === 'free_text' ? (
                <ClinicalHtmlView html={viewPrescription.body_text} sx={{ mb: 1.5 }} />
              ) : (viewPrescription.items || []).map((item: any, idx: number) => (
                <Card key={item.id || idx} sx={{ ...cloudApiCampaignCardSx, mb: 1.5, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {idx + 1}. {item.medication_name}
                      {item.concentration && ` ${item.concentration}`}
                      {item.pharmaceutical_form && ` - ${item.pharmaceutical_form}`}
                    </Typography>
                    {item.is_controlled && <Chip label="Controlado" size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />}
                  </Box>
                  {(item.instructions && isMemedPrescription(viewPrescription)) || composePosologyLine(item) ? (
                    <Typography variant="body2" sx={{ color: '#4b5563', whiteSpace: 'pre-line' }}>
                      <strong>Posologia:</strong>{' '}
                      {isMemedPrescription(viewPrescription) && item.instructions
                        ? item.instructions
                        : composePosologyLine(item)}
                    </Typography>
                  ) : null}
                  {formatPrescriptionItemQuantity(item) && (
                    <Typography variant="body2" sx={{ color: '#4b5563' }}>
                      <strong>Quantidade:</strong> {formatPrescriptionItemQuantity(item)}
                    </Typography>
                  )}
                </Card>
              ))}
              {isMemedPrescription(viewPrescription) && (viewPrescription.items || []).length === 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Os medicamentos desta receita não chegaram da Memed. Clique em sincronizar (ícone ↻ acima)
                  para recuperá-los.
                </Alert>
              )}

              {viewPrescription.diagnosis && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ ...cloudApiSectionTitleSx, mb: 1 }}>Informações Clínicas</Typography>
                  <Typography variant="body2">
                    <strong>Diagnóstico:</strong> {viewPrescription.diagnosis}
                    {viewPrescription.diagnosis_code ? ` (CID: ${viewPrescription.diagnosis_code})` : ''}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={cloudApiDialogFooterSx}>
          <Button
            onClick={() => { if (viewPrescription) void handlePrint(viewPrescription); }}
            startIcon={<PrintIcon />}
            sx={{ ...cloudApiOutlinedButtonSx, color: CLOUD_API_THEME.teal, border: 'none' }}
          >
            {viewPrescription && isMemedPrescription(viewPrescription)
              ? 'Abrir documento Memed'
              : viewPrescription && isSignedPrescription(viewPrescription)
                ? 'Abrir PDF assinado'
                : 'Imprimir'}
          </Button>
          {viewPrescription
            && viewPrescription.status === 'active'
            && !isMemedPrescription(viewPrescription)
            && !isSignedPrescription(viewPrescription) && (
            <Button
              onClick={() => { openSignDialog(viewPrescription); setViewOpen(false); }}
              startIcon={<SignIcon />}
              sx={{ ...cloudApiOutlinedButtonSx, color: CLOUD_API_THEME.teal, border: 'none' }}
            >
              Assinar
            </Button>
          )}
          {viewPrescription && !isMemedPrescription(viewPrescription) && !isSignedPrescription(viewPrescription) && (
            <Button
              onClick={() => { handleEdit(viewPrescription); setViewOpen(false); }}
              startIcon={<EditIcon />}
              sx={{ ...cloudApiOutlinedButtonSx, color: CLOUD_API_THEME.teal, border: 'none' }}
            >
              Editar
            </Button>
          )}
          <Button onClick={() => setViewOpen(false)} sx={cloudApiDialogCancelButtonSx}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
        <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
          Pré-visualização do Receituário
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {printHasMultipleVias && (
              <FormControlLabel
                control={<Switch size="small" checked={printFirstViaOnly} onChange={(e) => setPrintFirstViaOnly(e.target.checked)} />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Somente 1ª via</Typography>}
                sx={{ mr: 1 }}
              />
            )}
            <Button variant="contained" startIcon={<PrintIcon />} onClick={executePrint} sx={cloudApiPrimaryButtonSx}>
              Imprimir
            </Button>
            <IconButton onClick={() => setPrintOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: CLOUD_API_THEME.muted }}>
          {printData && (
            <iframe
              title="Pré-visualização do Receituário"
              srcDoc={buildPrescriptionPrintHtml(printData, { firstViaOnly: printFirstViaOnly })}
              style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={multiMonthOpen}
        onClose={() => !multiMonthSaving && setMultiMonthOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: cloudApiDialogPaperSx }}
      >
        <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MultiMonthIcon sx={cloudApiDialogHeaderIconSx} />
            Emitir para vários meses
          </Box>
          <IconButton onClick={() => !multiMonthSaving && setMultiMonthOpen(false)} disabled={multiMonthSaving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...cloudApiDialogContentSx, p: 3 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            A receita continua sendo um único registro. Na impressão, cada mês sai em uma página separada,
            com emissão e validade deslocadas mês a mês.
          </Alert>
          <TextField
            fullWidth
            type="number"
            label="Quantidade de meses"
            value={multiMonthValue}
            onChange={(e) => setMultiMonthValue(Number(e.target.value))}
            inputProps={{ min: 2, max: 12, step: 1 }}
            disabled={multiMonthSaving}
            sx={cloudApiInputFieldSx}
            helperText="Mínimo 2 meses. Máximo 12."
          />
        </DialogContent>
        <DialogActions sx={{ ...cloudApiDialogFooterSx, p: 3, gap: 1 }}>
          <Button onClick={() => setMultiMonthOpen(false)} disabled={multiMonthSaving} sx={cloudApiDialogCancelButtonSx}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConfirmMultiMonth()}
            disabled={multiMonthSaving || Number(multiMonthValue) < 2}
            startIcon={multiMonthSaving ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            sx={cloudApiDialogPrimaryButtonSx}
          >
            {multiMonthSaving ? 'Salvando...' : 'Salvar e imprimir'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrescriptionActionsBar;
